import type { UseMutationResult } from '@tanstack/react-query';
import { hasPushNotificationsEnabledAtom } from 'app/atoms';
import { type useOpenSettingsPrompt } from 'app/components/open-settings-prompt';
import { http } from 'app/lib/axios';
import { log } from 'app/lib/monitoring';
import { hasNotificationPermission as hasPermission, requestNotificationPermission } from 'app/lib/notifications';
import { pushNotifRegistrationInstance } from 'app/lib/push-notifications';
import { useAllAddresses } from 'app/network/address';
import { useListingVisibilityGroups } from 'app/network/groups';
import { useUser } from 'app/network/user';
import type { Preference } from 'app/types/preferences';
import { useOnePreference, useUpsertOnePreference } from 'app/network/preferences';
import { getAddressIcon } from 'app/screens/profile/my-clubs';
import { WhiteLabel } from 'app/types/white-labels';
import { FeatureFlag, isFeatureEnabled, isWhiteLabel } from 'app/utils/helpers';
import { isMobileNative, useUISize } from 'app/utils/web-helpers';
import { CustomerIO } from 'customerio-reactnative';
import { Image } from 'expo-image';
import * as Notifications from 'expo-notifications';
import { useAtom } from 'jotai';
import React, { useEffect, useEffectEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createTheme as createDSTheme } from 'app/shopify';
import { disabledGroupsEqual, normalizeDisabledGroups, reconcileDisabledGroups } from './disabled-groups';
import { NotificationPreferencesView } from './notification-preferences-view';
const theme = createDSTheme();
const NEED_SOURCE_ICON_SIZE = 30;
const NEED_SECTOR_ICON_SIZE = 16;

interface NotificationPreferenceValues {
  conciergerieOff: boolean | undefined;
  disabledNeedEmail: Preference<'emailForNeedDisabledGroups'>['value'] | undefined;
  disabledNeedPush: Preference<'pushNotificationsForNeedDisabledGroups'>['value'] | undefined;
  emailForInterests: boolean | undefined;
  emailForNeed: boolean | undefined;
  emailForUnseenChannel: boolean | undefined;
  emailForUrgentOffers: boolean | undefined;
  hasNotificationPermission: boolean;
  pushForInterests: boolean | undefined;
  pushForNeed: boolean | undefined;
  pushForUnseenChannel: boolean | undefined;
  pushForUrgentOffers: boolean | undefined;
  smsForChat: boolean | undefined;
  smsForInterests: boolean | undefined;
  smsForUrgentOffers: boolean | undefined;
}

const buildNotificationPreferences = (values: NotificationPreferenceValues) => ({
  messages: {
    push: (values.pushForUnseenChannel ?? true) && values.hasNotificationPermission,
    email: values.emailForUnseenChannel ?? true,
    sms: values.smsForChat ?? true,
  },
  conciergerie: {
    enabled: !(values.conciergerieOff ?? false),
  },
  needs: {
    push: (values.pushForNeed ?? true) && values.hasNotificationPermission,
    email: values.emailForNeed ?? true,
    disabledGroups: {
      push: normalizeDisabledGroups(values.disabledNeedPush),
      email: normalizeDisabledGroups(values.disabledNeedEmail),
    },
  },
  urgentOffers: {
    push: (values.pushForUrgentOffers ?? true) && values.hasNotificationPermission,
    email: values.emailForUrgentOffers ?? true,
    sms: values.smsForUrgentOffers ?? true,
  },
  interests: {
    push: (values.pushForInterests ?? true) && (!isMobileNative || values.hasNotificationPermission),
    email: values.emailForInterests ?? false,
    sms: values.smsForInterests ?? true,
  },
});

interface NotificationPreferencesProps {
  openSettingsPrompt: ReturnType<typeof useOpenSettingsPrompt>['openSettingsPrompt'];
}
const useNotificationPreferencesModel = ({ openSettingsPrompt }: NotificationPreferencesProps) => {
  const { t } = useTranslation('notifications-setting');
  const { t: tPref } = useTranslation('my-preferences');
  const { data: currentUser } = useUser();
  const interestAlertsEnabled = isFeatureEnabled(FeatureFlag.interestAlerts, currentUser?.featureFlags);
  const usesSectorNeedSources = isWhiteLabel(WhiteLabel.HauteGaspesie);

  const [hasNotificationPermission, setHasNotificationPermission] = useAtom(hasPushNotificationsEnabledAtom);

  const pushForUnseenChannelPreferenceQuery = useOnePreference('pushNotificationsForChat');
  const upsertPushForUnseenChannelPreference = useUpsertOnePreference('pushNotificationsForChat');
  const emailForUnseenChannelPreferenceQuery = useOnePreference('emailForUnseenChannel');
  const upsertEmailForUnseenChannelPreference = useUpsertOnePreference('emailForUnseenChannel');

  // Hidden: weekly new-offers communication (push + email). Uncomment to re-enable.
  // const pushForWeeklyOffersPreferenceQuery = useOnePreference('pushNotificationForWeeklyOffers');
  // const upsertPushForWeeklyOffersPreference = useUpsertOnePreference('pushNotificationForWeeklyOffers');
  // const emailForWeeklyOffersPreferenceQuery = useOnePreference('emailForWeeklyOffers');
  // const upsertEmailForWeeklyOffersPreference = useUpsertOnePreference('emailForWeeklyOffers');

  const pushForNeedPreferenceQuery = useOnePreference('pushNotificationsForNeed');
  const upsertPushForNeedPreference = useUpsertOnePreference('pushNotificationsForNeed');
  const emailForNeedPreferenceQuery = useOnePreference('emailForNeed');
  const upsertEmailForNeedPreference = useUpsertOnePreference('emailForNeed');

  const disabledGroupsNeedPush = useOnePreference('pushNotificationsForNeedDisabledGroups');
  const upsertDisabledGroupsNeedPush = useUpsertOnePreference('pushNotificationsForNeedDisabledGroups');

  const disabledGroupsNeedEmail = useOnePreference('emailForNeedDisabledGroups');
  const upsertDisabledGroupsNeedEmail = useUpsertOnePreference('emailForNeedDisabledGroups');
  const reconciledPreferenceSources = useRef<{
    push?: typeof disabledGroupsNeedPush.data;
    email?: typeof disabledGroupsNeedEmail.data;
  }>({});

  const smsForChatPreferenceQuery = useOnePreference('smsForChat');
  const upsertSmsForChatPreference = useUpsertOnePreference('smsForChat');
  const conciergerieOffPreferenceQuery = useOnePreference('conciergerieOff');
  const upsertConciergerieOffPreference = useUpsertOnePreference('conciergerieOff');

  const pushForUrgentOffersPreferenceQuery = useOnePreference('pushNotificationsForUrgentOffers');
  const upsertPushForUrgentOffersPreference = useUpsertOnePreference('pushNotificationsForUrgentOffers');
  const emailForUrgentOffersPreferenceQuery = useOnePreference('emailForUrgentOffers');
  const upsertEmailForUrgentOffersPreference = useUpsertOnePreference('emailForUrgentOffers');
  const smsForUrgentOffersPreferenceQuery = useOnePreference('smsForUrgentOffers');
  const upsertSmsForUrgentOffersPreference = useUpsertOnePreference('smsForUrgentOffers');

  const pushForInterestsPreferenceQuery = useOnePreference('pushNotificationsForInterests');
  const upsertPushForInterestsPreference = useUpsertOnePreference('pushNotificationsForInterests');
  const emailForInterestsPreferenceQuery = useOnePreference('emailForInterests');
  const upsertEmailForInterestsPreference = useUpsertOnePreference('emailForInterests');
  const smsForInterestsPreferenceQuery = useOnePreference('smsForInterests');
  const upsertSmsForInterestsPreference = useUpsertOnePreference('smsForInterests');

  useEffect(() => {
    const checkForNotificationPermission = async () => {
      const notificationsEnabled = await hasPermission();
      // If the user has changed their phone notification settings, reflect that.
      if (hasNotificationPermission !== notificationsEnabled) {
        upsertPushForUnseenChannelPreference.mutate(notificationsEnabled);
        // upsertPushForWeeklyOffersPreference.mutate(notificationsEnabled);
        upsertPushForNeedPreference.mutate(notificationsEnabled);
        upsertPushForUrgentOffersPreference.mutate(notificationsEnabled);
        if (interestAlertsEnabled) {
          upsertPushForInterestsPreference.mutate(notificationsEnabled);
        }
        setHasNotificationPermission(notificationsEnabled);
      }
    };
    void checkForNotificationPermission();
  }, [
    hasNotificationPermission,
    setHasNotificationPermission,
    interestAlertsEnabled,
    upsertPushForInterestsPreference,
    upsertPushForNeedPreference,
    upsertPushForUrgentOffersPreference,
    upsertPushForUnseenChannelPreference,
    // upsertPushForWeeklyOffersPreference,
  ]);

  const initialPreferenceValues: NotificationPreferenceValues = {
    conciergerieOff: conciergerieOffPreferenceQuery.data?.value,
    disabledNeedEmail: disabledGroupsNeedEmail.data?.value,
    disabledNeedPush: disabledGroupsNeedPush.data?.value,
    emailForInterests: emailForInterestsPreferenceQuery.data?.value,
    emailForNeed: emailForNeedPreferenceQuery.data?.value,
    emailForUnseenChannel: emailForUnseenChannelPreferenceQuery.data?.value,
    emailForUrgentOffers: emailForUrgentOffersPreferenceQuery.data?.value,
    hasNotificationPermission,
    pushForInterests: pushForInterestsPreferenceQuery.data?.value,
    pushForNeed: pushForNeedPreferenceQuery.data?.value,
    pushForUnseenChannel: pushForUnseenChannelPreferenceQuery.data?.value,
    pushForUrgentOffers: pushForUrgentOffersPreferenceQuery.data?.value,
    smsForChat: smsForChatPreferenceQuery.data?.value,
    smsForInterests: smsForInterestsPreferenceQuery.data?.value,
    smsForUrgentOffers: smsForUrgentOffersPreferenceQuery.data?.value,
  };
  const [preferences, setPreferences] = useState(() => buildNotificationPreferences(initialPreferenceValues));

  useEffect(() => {
    let cancelled = false;

    void Promise.resolve().then(() => {
      if (cancelled) return;

      setPreferences(
        buildNotificationPreferences({
          conciergerieOff: conciergerieOffPreferenceQuery.data?.value,
          disabledNeedEmail: disabledGroupsNeedEmail.data?.value,
          disabledNeedPush: disabledGroupsNeedPush.data?.value,
          emailForInterests: emailForInterestsPreferenceQuery.data?.value,
          emailForNeed: emailForNeedPreferenceQuery.data?.value,
          emailForUnseenChannel: emailForUnseenChannelPreferenceQuery.data?.value,
          emailForUrgentOffers: emailForUrgentOffersPreferenceQuery.data?.value,
          hasNotificationPermission,
          pushForInterests: pushForInterestsPreferenceQuery.data?.value,
          pushForNeed: pushForNeedPreferenceQuery.data?.value,
          pushForUnseenChannel: pushForUnseenChannelPreferenceQuery.data?.value,
          pushForUrgentOffers: pushForUrgentOffersPreferenceQuery.data?.value,
          smsForChat: smsForChatPreferenceQuery.data?.value,
          smsForInterests: smsForInterestsPreferenceQuery.data?.value,
          smsForUrgentOffers: smsForUrgentOffersPreferenceQuery.data?.value,
        }),
      );
    });

    return () => {
      cancelled = true;
    };
  }, [
    pushForUnseenChannelPreferenceQuery.data,
    emailForUnseenChannelPreferenceQuery.data,
    smsForChatPreferenceQuery.data,
    // pushForWeeklyOffersPreferenceQuery.data,
    // emailForWeeklyOffersPreferenceQuery.data,
    conciergerieOffPreferenceQuery.data,
    pushForNeedPreferenceQuery.data,
    emailForNeedPreferenceQuery.data,
    disabledGroupsNeedPush.data,
    disabledGroupsNeedEmail.data,
    pushForUrgentOffersPreferenceQuery.data,
    emailForUrgentOffersPreferenceQuery.data,
    smsForUrgentOffersPreferenceQuery.data,
    pushForInterestsPreferenceQuery.data,
    emailForInterestsPreferenceQuery.data,
    smsForInterestsPreferenceQuery.data,
    hasNotificationPermission,
  ]);

  const askForPushPermission = async ({
    onError,
    rollbackLocalPreference,
  }: {
    onError: () => void;
    rollbackLocalPreference?: () => void;
  }) => {
    if (hasNotificationPermission) {
      void pushNotifRegistrationInstance(http).registerForRemoteNotifications();
      return;
    }

    const settings = await Notifications.getPermissionsAsync();
    if (!settings.granted) {
      let permissionGranted = false;
      if (settings.canAskAgain) {
        // Ask the user to grant notif permission
        permissionGranted = await requestNotificationPermission();
      } else {
        // Failed to grant notif permission. Need to ask user to enable notifications in the app settings.
        openSettingsPrompt();
      }

      if (!permissionGranted) {
        if (rollbackLocalPreference) {
          rollbackLocalPreference();
        }
        onError();
      }
    }
  };

  const togglesForUnseenChannel = (() => {
    return [
      ...(isMobileNative
        ? [
            {
              label: t('pushNotif'),
              value: preferences.messages.push,

              onValueChange: async (v: boolean) => {
                setPreferences((prev: typeof preferences) => ({
                  ...prev,
                  messages: { ...prev.messages, push: v },
                }));
                upsertPushForUnseenChannelPreference.mutate(v, {
                  onError: () => {
                    setPreferences((prev: typeof preferences) => ({
                      ...prev,
                      messages: { ...prev.messages, push: !v },
                    }));
                  },
                });

                if (v) {
                  await askForPushPermission({
                    onError: () => upsertPushForUnseenChannelPreference.mutate(false),
                  });
                }
              },
            },
          ]
        : []),
      {
        label: t('email'),
        value: preferences.messages.email,
        onValueChange: (v: boolean) => {
          setPreferences((prev: typeof preferences) => ({
            ...prev,
            messages: { ...prev.messages, email: v },
          }));
          upsertEmailForUnseenChannelPreference.mutate(v, {
            onError: () => {
              setPreferences((prev: typeof preferences) => ({
                ...prev,
                messages: { ...prev.messages, email: !v },
              }));
            },
          });
        },
      },
      {
        label: t('sms'),
        value: preferences.messages.sms,
        onValueChange: (v: boolean) => {
          setPreferences((prev: typeof preferences) => ({
            ...prev,
            messages: { ...prev.messages, sms: v },
          }));
          upsertSmsForChatPreference.mutate(v, {
            onError: () => {
              setPreferences((prev: typeof preferences) => ({
                ...prev,
                messages: { ...prev.messages, sms: !v },
              }));
            },
          });
        },
      },
    ];
  })();

  // Hidden: weekly new-offers communication (push + email). Uncomment to re-enable.
  // const togglesForWeeklyOffers = useMemo(() => {
  //   return [
  //     ...(isMobileNative
  //       ? [
  //           {
  //             label: t('pushNotif'),
  //             value: preferences.newItems.push,
  //             // eslint-disable-next-line @typescript-eslint/no-misused-promises
  //             onValueChange: async (v: boolean) => {
  //               setPreferences((prev: typeof preferences) => ({
  //                 ...prev,
  //                 newItems: { ...prev.newItems, push: v },
  //               }));
  //               upsertPushForWeeklyOffersPreference.mutate(v, {
  //                 onError: () => {
  //                   setPreferences((prev: typeof preferences) => ({
  //                     ...prev,
  //                     newItems: { ...prev.newItems, push: !v },
  //                   }));
  //                 },
  //               });
  //
  //               if (v) {
  //                 await askForPushPermission({
  //                   onError: () => upsertPushForWeeklyOffersPreference.mutate(false),
  //                 });
  //               }
  //             },
  //           },
  //         ]
  //       : []),
  //     {
  //       label: t('email'),
  //       value: preferences.newItems.email,
  //       onValueChange: (v: boolean) => {
  //         setPreferences((prev: typeof preferences) => ({
  //           ...prev,
  //           newItems: { ...prev.newItems, email: v },
  //         }));
  //         upsertEmailForWeeklyOffersPreference.mutate(v, {
  //           onError: () => {
  //             setPreferences((prev: typeof preferences) => ({
  //               ...prev,
  //               newItems: { ...prev.newItems, email: !v },
  //             }));
  //           },
  //         });
  //       },
  //     },
  //   ];
  // }, [
  //   askForPushPermission,
  //   t,
  //   upsertEmailForWeeklyOffersPreference,
  //   upsertPushForWeeklyOffersPreference,
  //   preferences.newItems.push,
  //   preferences.newItems.email,
  // ]);

  const onTogglePush = async (v: boolean) => {
    setPreferences((prev: typeof preferences) => ({
      ...prev,
      needs: { ...prev.needs, push: v },
    }));
    upsertPushForNeedPreference.mutate(v, {
      onError: () => {
        setPreferences((prev: typeof preferences) => ({
          ...prev,
          needs: { ...prev.needs, push: !v },
        }));
      },
    });
    if (isMobileNative) {
      log(`[CustomerIO.setProfileAttributes] pushNotificationsForNeed: ${JSON.stringify(v)}`);
      void CustomerIO.setProfileAttributes({ pushNotificationsForNeed: v });
    }

    if (v) {
      await askForPushPermission({
        onError: () => upsertPushForNeedPreference.mutate(false),
      });
    }
  };

  const onToggleEmail = (v: boolean) => {
    setPreferences((prev: typeof preferences) => ({
      ...prev,
      needs: { ...prev.needs, email: v },
    }));
    upsertEmailForNeedPreference.mutate(v, {
      onError: () => {
        setPreferences((prev: typeof preferences) => ({
          ...prev,
          needs: { ...prev.needs, email: !v },
        }));
      },
    });
  };

  // Reconciliation needs the latest handlers without turning them into effect dependencies.
  const disableEmailForReconciliation = useEffectEvent(() => onToggleEmail(false));
  const disablePushForReconciliation = useEffectEvent(() => void onTogglePush(false));

  // User can control need notifications per address and visible group.
  // Haute-Gaspésie uses sector groups only, excluding the universal MRC club.
  const { data: addressesForNeed } = useAllAddresses();
  const { data: groupsForNeed } = useListingVisibilityGroups(true);
  const allGroupsAndAddressesForNeed = (() => {
    return [
      ...(addressesForNeed?.map((address) => ({
        id: address.id,
        type: 'address' as const,
        image: getAddressIcon(
          address,
          usesSectorNeedSources ? NEED_SECTOR_ICON_SIZE : NEED_SOURCE_ICON_SIZE,
          usesSectorNeedSources ? theme.colors.white : undefined,
        ),
        name: address.name ?? t('neighbourhoodClubName', { ns: 'my-clubs' }),
      })) ?? []),
      ...(groupsForNeed?.map((group) => ({
        id: group.id,
        type: 'group' as const,
        image: (
          <Image
            source={group.picture ? { uri: group.picture } : undefined}
            tintColor={usesSectorNeedSources ? theme.colors.white : undefined}
            style={{
              width: usesSectorNeedSources ? NEED_SECTOR_ICON_SIZE : NEED_SOURCE_ICON_SIZE,
              height: usesSectorNeedSources ? NEED_SECTOR_ICON_SIZE : NEED_SOURCE_ICON_SIZE,
              borderRadius: usesSectorNeedSources ? 0 : 8,
              borderWidth: usesSectorNeedSources ? 0 : 1,
              borderColor: usesSectorNeedSources ? undefined : theme.colors['surface.disabled'],
            }}
          />
        ),

        name: group.name,
      })) ?? []),
    ];
  })();

  const reconciledDisabledGroups = {
    push: reconcileDisabledGroups(preferences.needs.disabledGroups.push, allGroupsAndAddressesForNeed),
    email: reconcileDisabledGroups(preferences.needs.disabledGroups.email, allGroupsAndAddressesForNeed),
  };

  const valueForGroupOrAddress = (groupType: 'group' | 'address', id: string, type: 'push' | 'email') =>
    !Boolean(
      reconciledDisabledGroups[type].find(
        (p: { type: 'group' | 'address'; id: string }) => p.type === groupType && p.id === id,
      ),
    );

  const onValueChangeForGroupOrAddress = <
    T extends 'pushNotificationsForNeedDisabledGroups' | 'emailForNeedDisabledGroups',
  >(
    upsertQuery: UseMutationResult<Preference<T>, Error, Preference<T>['value']>,
    key: 'push' | 'email',
    groupType: 'group' | 'address',
    id: string,
    v: boolean,
    onAllDisabled: () => void,
  ) => {
    const oldValue = reconciledDisabledGroups[key];
    let newValue: Preference<T>['value'];
    if (v) {
      // Remove group/address from disabledGroups
      newValue = oldValue.filter(
        (p: { type: 'group' | 'address'; id: string }) => !(p.type === groupType && p.id === id),
      );
    } else {
      // Add group/address to disabledGroups
      newValue = [...oldValue, { type: groupType, id }];
    }
    setPreferences((prev: typeof preferences) => ({
      ...prev,
      needs: {
        ...prev.needs,
        disabledGroups: {
          ...prev.needs.disabledGroups,
          [key]: newValue,
        },
      },
    }));
    upsertQuery.mutate(newValue, {
      onError: () => {
        // Revert the change if the upsert fails
        setPreferences((prev: typeof preferences) => ({
          ...prev,
          needs: {
            ...prev.needs,
            disabledGroups: {
              ...prev.needs.disabledGroups,
              [key]: oldValue,
            },
          },
        }));
      },
    });
    const disabledItems = new Set(newValue.map((item) => `${item.type}:${item.id}`));
    const allItemsDisabled =
      allGroupsAndAddressesForNeed.length > 0 &&
      allGroupsAndAddressesForNeed.every((item) => disabledItems.has(`${item.type}:${item.id}`));
    if (!v && allItemsDisabled) {
      onAllDisabled();
    }
  };

  const togglesForNeed = (() => {
    return [
      ...(isMobileNative
        ? [
            {
              label: t('pushNotif'),
              value: preferences.needs.push,
              onValueChange: (v: boolean) => void onTogglePush(v),
              groups: allGroupsAndAddressesForNeed.map(
                (g: { id: string; name: string; image: React.ReactNode; type: 'group' | 'address' }) => ({
                  id: g.id,
                  type: g.type,
                  label: g.name,
                  image: g.image,
                  value: valueForGroupOrAddress(g.type, g.id, 'push'),
                  onValueChange: (v: boolean) =>
                    onValueChangeForGroupOrAddress<'pushNotificationsForNeedDisabledGroups'>(
                      upsertDisabledGroupsNeedPush as UseMutationResult<
                        Preference<'pushNotificationsForNeedDisabledGroups'>,
                        Error,
                        { type: 'group' | 'address'; id: string }[]
                      >,
                      'push',
                      g.type,
                      g.id,
                      v,
                      () => void onTogglePush(false),
                    ),
                }),
              ),
            },
          ]
        : []),
      {
        label: t('email'),
        value: preferences.needs.email,
        onValueChange: onToggleEmail,
        groups: isWhiteLabel(WhiteLabel.Refrain)
          ? []
          : allGroupsAndAddressesForNeed.map(
              (g: { id: string; name: string; image: React.ReactNode; type: 'group' | 'address' }) => ({
                id: g.id,
                type: g.type,
                label: g.name,
                image: g.image,
                value: valueForGroupOrAddress(g.type, g.id, 'email'),
                onValueChange: (v: boolean) =>
                  onValueChangeForGroupOrAddress<'emailForNeedDisabledGroups'>(
                    upsertDisabledGroupsNeedEmail as UseMutationResult<
                      Preference<'emailForNeedDisabledGroups'>,
                      Error,
                      { type: 'group' | 'address'; id: string }[]
                    >,
                    'email',
                    g.type,
                    g.id,
                    v,
                    () => onToggleEmail(false),
                  ),
              }),
            ),
      },
    ];
  })();

  const togglesForConciergerie = (() => {
    return [
      {
        label: t('conciergerieToggle'),
        value: preferences.conciergerie.enabled,
        onValueChange: (v: boolean) => {
          setPreferences((prev: typeof preferences) => ({
            ...prev,
            conciergerie: { enabled: v },
          }));
          upsertConciergerieOffPreference.mutate(!v, {
            onError: () => {
              setPreferences((prev: typeof preferences) => ({
                ...prev,
                conciergerie: { enabled: !v },
              }));
            },
          });
        },
      },
    ];
  })();

  const togglesForUrgentOffers = (() => {
    return [
      ...(isMobileNative
        ? [
            {
              label: t('pushNotif'),
              value: preferences.urgentOffers.push,

              onValueChange: async (v: boolean) => {
                setPreferences((prev: typeof preferences) => ({
                  ...prev,
                  urgentOffers: { ...prev.urgentOffers, push: v },
                }));
                upsertPushForUrgentOffersPreference.mutate(v, {
                  onError: () => {
                    setPreferences((prev: typeof preferences) => ({
                      ...prev,
                      urgentOffers: { ...prev.urgentOffers, push: !v },
                    }));
                  },
                });

                if (v) {
                  await askForPushPermission({
                    onError: () => upsertPushForUrgentOffersPreference.mutate(false),
                    rollbackLocalPreference: () => {
                      setPreferences((prev: typeof preferences) => ({
                        ...prev,
                        urgentOffers: { ...prev.urgentOffers, push: false },
                      }));
                    },
                  });
                }
              },
            },
          ]
        : []),
      {
        label: t('email'),
        value: preferences.urgentOffers.email,
        onValueChange: (v: boolean) => {
          setPreferences((prev: typeof preferences) => ({
            ...prev,
            urgentOffers: { ...prev.urgentOffers, email: v },
          }));
          upsertEmailForUrgentOffersPreference.mutate(v, {
            onError: () => {
              setPreferences((prev: typeof preferences) => ({
                ...prev,
                urgentOffers: { ...prev.urgentOffers, email: !v },
              }));
            },
          });
        },
      },
      {
        label: t('sms'),
        value: preferences.urgentOffers.sms,
        onValueChange: (v: boolean) => {
          setPreferences((prev: typeof preferences) => ({
            ...prev,
            urgentOffers: { ...prev.urgentOffers, sms: v },
          }));
          upsertSmsForUrgentOffersPreference.mutate(v, {
            onError: () => {
              setPreferences((prev: typeof preferences) => ({
                ...prev,
                urgentOffers: { ...prev.urgentOffers, sms: !v },
              }));
            },
          });
        },
      },
    ];
  })();

  const togglesForInterests = (() => {
    return [
      {
        label: t('pushNotif'),
        value: preferences.interests.push,

        onValueChange: async (v: boolean) => {
          setPreferences((prev: typeof preferences) => ({
            ...prev,
            interests: { ...prev.interests, push: v },
          }));
          upsertPushForInterestsPreference.mutate(v, {
            onError: () => {
              setPreferences((prev: typeof preferences) => ({
                ...prev,
                interests: { ...prev.interests, push: !v },
              }));
            },
          });

          if (v && isMobileNative) {
            await askForPushPermission({
              onError: () => upsertPushForInterestsPreference.mutate(false),
              rollbackLocalPreference: () => {
                setPreferences((prev: typeof preferences) => ({
                  ...prev,
                  interests: { ...prev.interests, push: false },
                }));
              },
            });
          }
        },
      },
      {
        label: t('email'),
        value: preferences.interests.email,
        onValueChange: (v: boolean) => {
          setPreferences((prev: typeof preferences) => ({
            ...prev,
            interests: { ...prev.interests, email: v },
          }));
          upsertEmailForInterestsPreference.mutate(v, {
            onError: () => {
              setPreferences((prev: typeof preferences) => ({
                ...prev,
                interests: { ...prev.interests, email: !v },
              }));
            },
          });
        },
      },
      {
        label: t('sms'),
        value: preferences.interests.sms,
        onValueChange: (v: boolean) => {
          setPreferences((prev: typeof preferences) => ({
            ...prev,
            interests: { ...prev.interests, sms: v },
          }));
          upsertSmsForInterestsPreference.mutate(v, {
            onError: () => {
              setPreferences((prev: typeof preferences) => ({
                ...prev,
                interests: { ...prev.interests, sms: !v },
              }));
            },
          });
        },
      },
    ];
  })();

  // Keep persisted disabled groups aligned with the groups visible to this user.
  // The callback above handles new changes immediately; this also reconciles preferences loaded with stale group IDs.
  useEffect(() => {
    if (addressesForNeed === undefined || groupsForNeed === undefined) return undefined;

    let cancelled = false;

    void Promise.resolve().then(() => {
      if (cancelled) return;

      const allEmailGroupsDisabled =
        allGroupsAndAddressesForNeed.length > 0 &&
        reconciledDisabledGroups.email.length === allGroupsAndAddressesForNeed.length;
      if (allEmailGroupsDisabled && preferences.needs.email) {
        disableEmailForReconciliation();
      }
      const emailPreferenceNeedsReconciliation =
        disabledGroupsNeedEmail.data !== undefined &&
        disabledGroupsEqual(
          normalizeDisabledGroups(disabledGroupsNeedEmail.data.value),
          preferences.needs.disabledGroups.email,
        ) &&
        !disabledGroupsEqual(disabledGroupsNeedEmail.data.value, reconciledDisabledGroups.email);
      if (
        emailPreferenceNeedsReconciliation &&
        reconciledPreferenceSources.current.email !== disabledGroupsNeedEmail.data
      ) {
        reconciledPreferenceSources.current.email = disabledGroupsNeedEmail.data;
        if (!disabledGroupsEqual(preferences.needs.disabledGroups.email, reconciledDisabledGroups.email)) {
          setPreferences((prev: typeof preferences) => ({
            ...prev,
            needs: {
              ...prev.needs,
              disabledGroups: {
                ...prev.needs.disabledGroups,
                email: reconciledDisabledGroups.email,
              },
            },
          }));
        }
        upsertDisabledGroupsNeedEmail.mutate(reconciledDisabledGroups.email);
      } else if (!emailPreferenceNeedsReconciliation) {
        reconciledPreferenceSources.current.email = undefined;
      }

      const allPushGroupsDisabled =
        allGroupsAndAddressesForNeed.length > 0 &&
        reconciledDisabledGroups.push.length === allGroupsAndAddressesForNeed.length;
      if (allPushGroupsDisabled && preferences.needs.push) {
        disablePushForReconciliation();
      }
      const pushPreferenceNeedsReconciliation =
        disabledGroupsNeedPush.data !== undefined &&
        disabledGroupsEqual(
          normalizeDisabledGroups(disabledGroupsNeedPush.data.value),
          preferences.needs.disabledGroups.push,
        ) &&
        !disabledGroupsEqual(disabledGroupsNeedPush.data.value, reconciledDisabledGroups.push);
      if (
        pushPreferenceNeedsReconciliation &&
        reconciledPreferenceSources.current.push !== disabledGroupsNeedPush.data
      ) {
        reconciledPreferenceSources.current.push = disabledGroupsNeedPush.data;
        if (!disabledGroupsEqual(preferences.needs.disabledGroups.push, reconciledDisabledGroups.push)) {
          setPreferences((prev: typeof preferences) => ({
            ...prev,
            needs: {
              ...prev.needs,
              disabledGroups: {
                ...prev.needs.disabledGroups,
                push: reconciledDisabledGroups.push,
              },
            },
          }));
        }
        upsertDisabledGroupsNeedPush.mutate(reconciledDisabledGroups.push);
      } else if (!pushPreferenceNeedsReconciliation) {
        reconciledPreferenceSources.current.push = undefined;
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    addressesForNeed,
    groupsForNeed,
    disabledGroupsNeedEmail.data,
    disabledGroupsNeedPush.data,
    preferences.needs.disabledGroups.email,
    preferences.needs.disabledGroups.push,
    preferences.needs.email,
    preferences.needs.push,
    reconciledDisabledGroups.email,
    reconciledDisabledGroups.push,
    allGroupsAndAddressesForNeed,
    upsertDisabledGroupsNeedEmail,
    upsertDisabledGroupsNeedPush,
  ]);
  const { isLargeUI } = useUISize();

  return {
    interestAlertsEnabled,
    isLargeUI,
    pageTitle: tPref('notificationsButtonText'),
    togglesForConciergerie,
    togglesForInterests,
    togglesForNeed,
    togglesForUnseenChannel,
    togglesForUrgentOffers,
    usesSectorNeedSources,
  };
};

export const NotificationPreferences = (props: NotificationPreferencesProps) => {
  return <NotificationPreferencesView {...useNotificationPreferencesModel(props)} />;
};
